import type { OfflineDbExecutor } from '../driver-types';

/**
 * The plainly-shaped families: bootstrap set (profile, memberships, account
 * METADATA - the credential axis never lands in this file, even encrypted),
 * tasks and notifications. All the same contract: upsert cached server rows
 * as JSON. Org-scoped families list with the organization_id filter; the
 * bootstrap set (the signed-in user's own profile and memberships) is
 * user-scoped by nature and lists across orgs.
 */
export interface CachedRow {
  readonly id: string;
  readonly payload: string;
}

// These two unions are the SQL-injection guard on every `${table}`
// interpolation below - the SELECT takes the wide one, the batched INSERT and
// the DELETE the narrow one - so only a literal from this file can reach one.
type JsonRowTable = 'email_accounts_meta' | 'tasks' | 'notifications';

/**
 * The subset whose rows carry `synced_at` and whose family a list cache
 * REPLACES wholesale. `email_accounts_meta` has no such column (see
 * `schema.ts`), so it is deliberately outside this type - the batched upsert
 * below binds `synced_at` unconditionally.
 */
export type JsonRowListTable = 'tasks' | 'notifications';

/**
 * Upsert a whole list in ONE native call. On native every `run` is a Capacitor
 * bridge round-trip, so a per-row loop turns a cached task board into an N+1 -
 * the reason `email_index` batches too.
 */
export async function upsertJsonRows(
  driver: OfflineDbExecutor,
  table: JsonRowListTable,
  organizationId: string,
  rows: readonly CachedRow[],
  nowMs: number,
): Promise<void> {
  if (rows.length === 0) {
    return;
  }
  await driver.runBatch(
    rows.map((row) => ({
      statement: `INSERT INTO ${table} (id, organization_id, synced_at, payload) VALUES (?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET organization_id = excluded.organization_id, synced_at = excluded.synced_at, payload = excluded.payload`,
      values: [row.id, organizationId, nowMs, row.payload],
    })),
  );
}

/** Every cached row of one family for an organization. */
export async function listJsonRows(
  driver: OfflineDbExecutor,
  table: JsonRowTable,
  organizationId: string,
): Promise<CachedRow[]> {
  const rows = await driver.query<{ id: string; payload: string }>(
    `SELECT id, payload FROM ${table} WHERE organization_id = ?`,
    [organizationId],
  );
  return rows.map((row) => ({ id: row.id, payload: row.payload }));
}

/**
 * Drop every cached row of one family for an organization.
 *
 * The family holds ONE view's rows at a time: a hydration write purges before
 * it repopulates, so rows the server has since dropped cannot linger and two
 * differently-filtered views cannot blend into a list that matches neither.
 */
export async function deleteJsonRows(
  driver: OfflineDbExecutor,
  table: JsonRowListTable,
  organizationId: string,
): Promise<void> {
  await driver.run(`DELETE FROM ${table} WHERE organization_id = ?`, [organizationId]);
}

export async function upsertBootstrapProfile(
  driver: OfflineDbExecutor,
  userId: string,
  payload: string,
): Promise<void> {
  await driver.run(
    `INSERT INTO bootstrap_profile (user_id, payload) VALUES (?, ?)
     ON CONFLICT(user_id) DO UPDATE SET payload = excluded.payload`,
    [userId, payload],
  );
}

export async function getBootstrapProfile(
  driver: OfflineDbExecutor,
  userId: string,
): Promise<string | null> {
  const rows = await driver.query<{ payload: string }>(
    'SELECT payload FROM bootstrap_profile WHERE user_id = ?',
    [userId],
  );
  return rows[0]?.payload ?? null;
}

export async function upsertOrgMembership(
  driver: OfflineDbExecutor,
  organizationId: string,
  payload: string,
): Promise<void> {
  await driver.run(
    `INSERT INTO org_memberships (organization_id, payload) VALUES (?, ?)
     ON CONFLICT(organization_id) DO UPDATE SET payload = excluded.payload`,
    [organizationId, payload],
  );
}

/** Replace the complete active-membership snapshot in one native transaction. */
export async function replaceOrgMemberships(
  driver: OfflineDbExecutor,
  rows: readonly CachedRow[],
): Promise<void> {
  await driver.runBatch([
    { statement: 'DELETE FROM org_memberships' },
    ...rows.map((row) => ({
      statement:
        'INSERT INTO org_memberships (organization_id, payload) VALUES (?, ?) ON CONFLICT(organization_id) DO UPDATE SET payload = excluded.payload',
      values: [row.id, row.payload],
    })),
  ]);
}

export async function listOrgMemberships(driver: OfflineDbExecutor): Promise<CachedRow[]> {
  const rows = await driver.query<{ organization_id: string; payload: string }>(
    'SELECT organization_id, payload FROM org_memberships',
  );
  return rows.map((row) => ({ id: row.organization_id, payload: row.payload }));
}

/**
 * The ONLY shape that may land in email_accounts_meta. The server row for
 * an email account carries credential columns, so this repository breaks
 * the module's usual "opaque pre-serialized server row" contract on
 * purpose: it takes a typed object and serializes an explicit field pick,
 * so extra keys - tokens included - are dropped at runtime, not just
 * discouraged by the type.
 */
export interface EmailAccountMetaPayload {
  readonly id: string;
  readonly email: string;
  readonly provider: string | null;
  readonly status: string | null;
  readonly display_name: string | null;
  /** Shared vs personal - the send-authorization axis (ADR 0009). */
  readonly account_type: string | null;
}

export async function upsertEmailAccountMeta(
  driver: OfflineDbExecutor,
  organizationId: string,
  meta: EmailAccountMetaPayload,
): Promise<void> {
  const payload = JSON.stringify({
    id: meta.id,
    email: meta.email,
    provider: meta.provider,
    status: meta.status,
    display_name: meta.display_name,
    account_type: meta.account_type,
  });
  // meta.id is the single source of truth for the row key - a separate
  // accountId parameter could silently diverge from the serialized id.
  await driver.run(
    `INSERT INTO email_accounts_meta (id, organization_id, payload) VALUES (?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET organization_id = excluded.organization_id, payload = excluded.payload`,
    [meta.id, organizationId, payload],
  );
}

export async function listEmailAccountMeta(
  driver: OfflineDbExecutor,
  organizationId: string,
): Promise<CachedRow[]> {
  return await listJsonRows(driver, 'email_accounts_meta', organizationId);
}
